import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ApolloError, gql, useQuery } from '@apollo/client';
import { isNumber } from 'lodash';
import { QUERY_FETCH_POLICY } from 'core/hooks/useQueryHelper';
import { IChannelFromBE } from '../types/messages';
import { messagingActions } from '../redux/messagingActions';
import agreementSelectors from 'agreement/redux/agreementSelectors';
import generateChannelsMap from 'messaging/utils/messagingUtils';

export const ChannelsQuery = gql`
    query messagingChannelListBySaasEnvironment($rentalAgreementId: Int) {
        messagingChannelListBySaasEnvironment(rentalAgreementId: $rentalAgreementId) {
            channelId
            channelType
            lastActivity
            lastMessage
            messagesCount
            serviceProviderUsers {
                fullName
                externalUuid
            }
            serviceRequestData {
                id
                title
                type
            }
        }
    }
`;

export const AssistantConversationQuery = gql`
    query assistantConversation($messagingChannelId: String!) {
        assistantConversation(messagingChannelId: $messagingChannelId) {
            id
            muted
        }
    }
`;

export default function useQueryChannels(skip = false): {
    loading: boolean;
    error?: ApolloError;
    called: boolean;
    refetchAndUpdate: () => Promise<void>;
} {
    const dispatch = useDispatch();

    const selectedAgreementId = useSelector(agreementSelectors.selectedAgreementId);

    const { data, loading, error, called, refetch } = useQuery<{
        messagingChannelListBySaasEnvironment: IChannelFromBE[];
    }>(ChannelsQuery, {
        ...QUERY_FETCH_POLICY,
        variables: { rentalAgreementId: selectedAgreementId },
        skip: !isNumber(selectedAgreementId) || skip,
    });

    const generateAndDispatchChannelsMap = (data?: { messagingChannelListBySaasEnvironment: IChannelFromBE[] }) => {
        if (data) {
            const channelsMap = generateChannelsMap(data?.messagingChannelListBySaasEnvironment);
            dispatch(messagingActions.setChannelsMap({ channelsMap, selectedAgreementId }));
        }
    };

    useEffect(() => {
        generateAndDispatchChannelsMap(data);
    }, [data]);

    const refetchAndUpdate = () =>
        refetch()
            .then(({ data }) => {
                generateAndDispatchChannelsMap(data);
            })
            .catch((error) => {
                console.error(error);
            });

    return {
        loading,
        error,
        called,
        refetchAndUpdate,
    };
}
